/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node } from "@msdyn365-commerce-modules/utilities";
import * as React from "react";
import {
  ICheckoutSectionContainerItem,
  ICheckoutSectionContainerViewProps
} from "@msdyn365-commerce-modules/checkout/src/modules/checkout-section-container/./checkout-section-container";
import { ICheckoutSectionContainerData } from '@msdyn365-commerce-modules/checkout/src/modules/checkout-section-container/checkout-section-container.data';
import {
  Accordion,
  AccordionItem,
  AccordionItemButton,
  AccordionItemHeading,
  AccordionItemPanel
} from "react-accessible-accordion";
import get from "lodash/get";
import { ICheckoutContext, CheckoutContext } from "../../../utilities/CheckoutContext";
import {
  ICheckoutSectionContainerProps
} from "../definition-extensions/checkout-section-container.ext.props.autogenerated";
import { setLoyaltyCookie, setGiftCardCookie } from "../../../utilities/CheckoutCookieManager";

interface IAccordionItemSplit {
  accordionItems: ICheckoutSectionContainerItem[];
  nonAccordionItems: ICheckoutSectionContainerItem[];
}

interface IKlarnaIFrameWindow extends Window {
  redirect: () => void;
}

const _iframeQuerySelector = '.checkout-payment-instrument__iframe';

const ItemComponent: React.FC<ICheckoutSectionContainerItem> = ({ loading, itemProps, item }) => (
  <>
      {loading}
      <Node {...itemProps}>{item}</Node>
  </>
);

const getAccordionAndNonAccordionSplit = (items: ICheckoutSectionContainerItem[]): IAccordionItemSplit => {
  return items.reduce((acc, item) => {
    const isAccordion = get(item, 'item.props.config.putInAccordion');
    const key = isAccordion ? 'accordionItems' : 'nonAccordionItems';
    return {
      ...acc,
      [key]: [...(acc[key] || []), item]
    }
  }, {}) as IAccordionItemSplit;
}

const getAccordionHeading = (item: ICheckoutSectionContainerItem): string => get(item, 'item.props.config.accordionHeading') || '';

const isKlarnaConnector = (item: ICheckoutSectionContainerItem) => get(item, 'item.props.config.paymenTenderType')?.toLowerCase().includes('klarna');

const shouldRenderPaymentSection = (item: ICheckoutSectionContainerItem) => !!get(item, 'item.props.config.canPlaceOrder')

const CheckoutSectionContainerView: React.FC<ICheckoutSectionContainerViewProps & ICheckoutSectionContainerProps<ICheckoutSectionContainerData>> = (props) => {
  const { checkoutPlainContainerProps, items, data: { checkout }, context: { request: { cookies }}, config: { loyaltyTimeout } } = props;
  const checkoutContext = React.useContext<ICheckoutContext>(CheckoutContext)

  const itemsToRender = getAccordionAndNonAccordionSplit(items);
  // const termsSlot = rest.slots.termsAndConditions;

  const invokeKlarnaButtonClick = () => {
    if (checkout.result) {
      const { loyaltyAmount, giftCardExtends, checkoutCart: { cart: { Id: cartId}}} = checkout.result;
      setLoyaltyCookie(cookies, loyaltyTimeout || 20, loyaltyAmount, cartId);
      setGiftCardCookie(cookies, loyaltyTimeout || 20, giftCardExtends, cartId);
    }
    const frame = document.querySelector<HTMLIFrameElement>(_iframeQuerySelector);
    if (frame) {
      (frame.contentWindow as IKlarnaIFrameWindow)?.redirect();
    }
  };

  const getPlaceOrderButton = (item: ICheckoutSectionContainerItem) => {
    if (isKlarnaConnector(item)) {
      return React.cloneElement(checkoutContext.placeOrderButton as React.ReactElement, { placeOrder: invokeKlarnaButtonClick });
    } else {
      return checkoutContext.placeOrderButton;
    }
  };

  return (
    <Module {...checkoutPlainContainerProps}>
      {itemsToRender.nonAccordionItems.map(item => (
        <ItemComponent key={item.id} {...item} />
      ))}
      {checkoutContext?.cartHasBalance && itemsToRender.accordionItems.length && (
        <Accordion allowZeroExpanded={true}>
          {itemsToRender.accordionItems.map(item => (
            <AccordionItem>
              <AccordionItemHeading>
                <AccordionItemButton>
                  {getAccordionHeading(item)}
                </AccordionItemButton>
              </AccordionItemHeading>
              <AccordionItemPanel>
                <ItemComponent key={item.id} {...item} />
                {shouldRenderPaymentSection(item) && <div>
                  {checkoutContext.mailChimp}
                  {checkoutContext.termsAndConditions}
                  {getPlaceOrderButton(item)}
                </div>}
              </AccordionItemPanel>
            </AccordionItem>
          ))}
        </Accordion>
      )}

    </Module>
  );
};

export default CheckoutSectionContainerView;
